import { useCallback } from 'react'
import { useTracking } from './useTracking'
import { ANNOUNCEMENT_ID } from './use-whats-new-seen'
import type { WhatsNewFeature } from '@/components/whats-new/features'

/**
 * One source for every What's New analytics call.
 *
 * Both the pop-up and the page fire several near-identical Segment events, and
 * every one carries the same common shape (announcement id, org id, preview
 * flag, source). Binding those here means a call site supplies only what is
 * genuinely its own — a feature row its feature, a CTA nothing — and a future
 * caller cannot forget the common fields or drift the shape.
 */
export function useWhatsNewTracking(
  organisation_id: string,
  source: 'popup' | 'page'
) {
  const tracker = useTracking()

  const common = {
    announcement_id: ANNOUNCEMENT_ID,
    organisation_id,
    source,
  } as const

  const popupViewed = useCallback(
    (opts: { preview: boolean }) =>
      tracker.trackWhatsNewPopupViewed({ ...common, preview: opts.preview }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tracker, organisation_id, source]
  )

  const popupDismissed = useCallback(
    (opts: { preview: boolean }) =>
      tracker.trackWhatsNewPopupDismissed({ ...common, preview: opts.preview }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tracker, organisation_id, source]
  )

  const pageViewed = useCallback(
    () => tracker.trackWhatsNewPageViewed({ ...common }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tracker, organisation_id, source]
  )

  const featureClicked = useCallback(
    (feature: WhatsNewFeature, opts: { preview?: boolean } = {}) =>
      tracker.trackWhatsNewFeatureClicked({
        ...common,
        feature_id: feature.id,
        feature_name: feature.name,
        ...(opts.preview !== undefined ? { preview: opts.preview } : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tracker, organisation_id, source]
  )

  const salesClicked = useCallback(
    (opts: { preview?: boolean } = {}) =>
      tracker.trackWhatsNewSalesClicked({
        ...common,
        ...(opts.preview !== undefined ? { preview: opts.preview } : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tracker, organisation_id, source]
  )

  const seeAllClicked = useCallback(
    (opts: { preview?: boolean } = {}) =>
      tracker.trackWhatsNewSeeAllClicked({
        ...common,
        ...(opts.preview !== undefined ? { preview: opts.preview } : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tracker, organisation_id, source]
  )

  return {
    popupViewed,
    popupDismissed,
    pageViewed,
    featureClicked,
    salesClicked,
    seeAllClicked,
  }
}
